import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Teleport as _Teleport } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "viewer-overlay informe-print-target"
}
const _hoisted_2 = { class: "viewer-toolbar" }
const _hoisted_3 = { class: "toolbar-izq" }
const _hoisted_4 = { class: "doc-titulo" }
const _hoisted_5 = { class: "toolbar-centro" }
const _hoisted_6 = { class: "modos" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "toolbar-der" }
const _hoisted_9 = { class: "pag-conteo" }
const _hoisted_10 = ["disabled"]
const _hoisted_11 = ["disabled"]
const _hoisted_12 = { class: "viewer-body" }
const _hoisted_13 = ["data-pagina-id"]
const _hoisted_14 = {
  key: 0,
  class: "portada"
}
const _hoisted_15 = { class: "portada-cuerpo" }
const _hoisted_16 = { class: "portada-h1" }
const _hoisted_17 = {
  key: 1,
  class: "pag-body"
}
const _hoisted_18 = { class: "pag-titulo" }
const _hoisted_19 = {
  key: 0,
  class: "pag-bloques"
}
const _hoisted_20 = ["innerHTML"]
const _hoisted_21 = {
  key: 2,
  class: "pag-vacio"
}
const _hoisted_22 = {
  key: 2,
  class: "pag-body"
}
const _hoisted_23 = { class: "pag-titulo factor-titulo" }
const _hoisted_24 = {
  key: 0,
  class: "sub-sec"
}
const _hoisted_25 = { class: "sub-titulo" }
const _hoisted_26 = {
  key: 0,
  class: "pag-bloques"
}
const _hoisted_27 = ["innerHTML"]
const _hoisted_28 = { class: "caract-titulo" }
const _hoisted_29 = {
  key: 0,
  class: "pag-bloques"
}
const _hoisted_30 = ["innerHTML"]
const _hoisted_31 = {
  key: 2,
  class: "pag-vacio small"
}
const _hoisted_32 = { class: "pagina-pie" }
const _hoisted_33 = { class: "pie-titulo" }

import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import BloqueDatos from '@/components/BloqueDatos.vue'
import type { Informe } from '@/services/InformeService'

interface NodoArbol { id: number; nombre: string; hijos: Array<{ id: number; nombre: string }> }
interface BloqueDesc { id: string; tipo: string; params?: Record<string, any> }

interface Pagina {
  id: string
  tipo: 'portada' | 'general' | 'factor'
  titulo?: string
  campo?: string
  factor?: NodoArbol
}


export default /*@__PURE__*/_defineComponent({
  __name: 'VistaPreviewInforme',
  props: {
    visible: { type: Boolean },
    informe: {},
    factores: {},
    autoImprimir: { type: Boolean },
    autoDescargarWord: { type: Boolean }
  },
  emits: ["cerrar"],
  setup(__props: any, { emit: __emit }) {

/**
 * Previsualización del informe antes de exportarlo.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

const props = __props

const emit = __emit

const modoVista = ref<'simple' | 'doble'>('simple')

const modos: Array<{ id: 'simple' | 'doble'; label: string }> = [
  { id: 'simple', label: 'Una página' },
  { id: 'doble',  label: 'Dos páginas' },
]

const generales = [
  { campo: 'presentacion',         titulo: 'Presentación' },
  { campo: 'descripcion_programa', titulo: 'Descripción del programa' },
  { campo: 'analisis_resultados',  titulo: 'Análisis de resultados' },
  { campo: 'conclusion',           titulo: 'Conclusión' },
]

const bloquesFactor = [
  { clave: 'conclusiones',             titulo: 'Conclusiones' },
  { clave: 'fortalezas_edit',          titulo: 'Fortalezas' },
  { clave: 'debilidades_edit',         titulo: 'Debilidades' },
  { clave: 'acciones_fortalecimiento', titulo: 'Acciones de fortalecimiento' },
  { clave: 'acciones_mejoramiento',    titulo: 'Acciones de mejoramiento' },
  { clave: 'anexos',                   titulo: 'Anexos' },
]

const etiquetaEstado = computed(() =>
  ({ borrador: 'Borrador', en_revision: 'En revisión', avalado: 'Avalado' }[props.informe.estado ?? 'borrador'])
)

function storageKey(skey: string): string {
  return `informe-bloques-${props.informe.id_informe}-${skey}`
}

function bloquesFor(skey: string): BloqueDesc[] {
  try {
    const raw = localStorage.getItem(storageKey(skey))
    return raw ? JSON.parse(raw) : []
  } catch { return [] }
}

function tieneTextoBloque(skey: string): boolean {
  return bloquesFor(skey).some(b => b.tipo === 'TEXTO')
}

function textoGeneral(campo?: string): string | null {
  if (!campo) return null
  return (props.informe as Record<string, any>)[campo] ?? null
}

function redaccionHtml(idComponente: number, bloque: string): string | null {
  const r = props.informe.redacciones?.find(
    x => x.id_componente === idComponente && x.bloque === bloque
  )
  return r?.contenido ?? null
}

function buildPaginas(): Pagina[] {
  const result: Pagina[] = []
  result.push({ id: 'portada', tipo: 'portada' })
  for (const g of generales) {
    result.push({ id: `gen-${g.campo}`, tipo: 'general', titulo: g.titulo, campo: g.campo })
  }
  for (const f of props.factores) {
    result.push({ id: `factor-${f.id}`, tipo: 'factor', factor: f })
  }
  return result
}

const paginas = ref<Pagina[]>(buildPaginas())
const generandoPdf = ref(false)
const generandoWord = ref(false)
const paginasAreaRef = ref<HTMLElement | null>(null)

watch(() => props.visible, (newVal) => {
  if (newVal) paginas.value = buildPaginas()
})

// Impresion Nativa
function limpiarImpresion(): void {
  document.body.classList.remove('printing-informe')
  generandoPdf.value = false
}

async function imprimirPdf(): Promise<void> {
  if (generandoPdf.value) return
  generandoPdf.value = true

  // El modo simple garantiza ancho de hoja completo al imprimir
  const prevModo = modoVista.value
  modoVista.value = 'simple'

  document.body.classList.add('printing-informe')

  // Al cerrarse el diálogo de impresión, limpiamos el estado
  const onAfter = () => {
    limpiarImpresion()
    modoVista.value = prevModo
    window.removeEventListener('afterprint', onAfter)
  }
  window.addEventListener('afterprint', onAfter)

  // Espera para que el DOM aplique la clase y los bloques se rendericen antes de imprimir
  await new Promise(r => setTimeout(r, 300))
  window.print()
  setTimeout(() => {
    if (generandoPdf.value) {
      limpiarImpresion()
      modoVista.value = prevModo
      window.removeEventListener('afterprint', onAfter)
    }
  }, 1000)
}

// ── Exportación Word ──────────────────────────────────────────────

function svgAPngBuffer(
  svg: SVGSVGElement
): Promise<{ data: ArrayBuffer; width: number; height: number } | null> {
  return new Promise(resolve => {
    const rect = svg.getBoundingClientRect()
    const w = Math.max(rect.width, 300)
    const h = Math.max(rect.height, 150)
    const raw = new XMLSerializer().serializeToString(svg)
    const ns = raw.includes('xmlns=') ? raw : raw.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"')
    const blob = new Blob([ns], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = () => {
      const scale = 2
      const canvas = document.createElement('canvas')
      canvas.width = w * scale; canvas.height = h * scale
      const ctx = canvas.getContext('2d')!
      ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.scale(scale, scale); ctx.drawImage(img, 0, 0, w, h)
      URL.revokeObjectURL(url)
      canvas.toBlob(b => {
        if (!b) { resolve(null); return }
        b.arrayBuffer()
          .then(buf => resolve({ data: buf, width: Math.round(w), height: Math.round(h) }))
          .catch(() => resolve(null))
      }, 'image/png')
    }
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null) }
    img.src = url
  })
}

async function descargarWord(): Promise<void> {
  if (generandoWord.value) return
  generandoWord.value = true
  const prevModo = modoVista.value
  modoVista.value = 'simple'
  await new Promise(r => setTimeout(r, 300))

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const D = await import('docx') as any
    const {
      Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
      ImageRun, HeadingLevel, AlignmentType, WidthType, BorderStyle, ShadingType,
    } = D

    const area = paginasAreaRef.value
    if (!area) return

    // ── helpers ──────────────────────────────────────────────

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nodosARuns = (el: Node, fmt: { bold?: boolean; italics?: boolean } = {}): any[] => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const runs: any[] = []
      for (const n of Array.from(el.childNodes)) {
        if (n.nodeType === Node.TEXT_NODE) {
          const text = n.textContent ?? ''
          if (text) runs.push(new TextRun({ text, ...fmt, size: 22, font: 'Calibri' }))
        } else if (n.nodeType === Node.ELEMENT_NODE) {
          const tag = (n as Element).tagName.toUpperCase()
          const f = { ...fmt }
          if (['STRONG', 'B'].includes(tag)) f.bold = true
          if (['EM', 'I'].includes(tag)) f.italics = true
          runs.push(...nodosARuns(n, f))
        }
      }
      return runs
    }

    const cellFill = (cls: string, trCls: string): string => {
      const all = cls + ' ' + trCls
      if (all.includes('celda-factor'))     return 'f5f9fc'
      if (all.includes('fila-subtotal'))    return 'eaf5e9'
      if (all.includes('fila-global'))      return 'dcefff'
      if (all.includes('fila-caract-head')) return 'f5f5f5'
      if (all.includes('clas-fuerte'))      return 'd4edda'
      if (all.includes('clas-debil'))       return 'fff3cd'
      if (all.includes('clas-muy_debil'))   return 'f8d7da'
      return 'ffffff'
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tablaADocx = (tbl: HTMLTableElement): any => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rows: any[] = []
      for (const tr of Array.from(tbl.querySelectorAll('tr'))) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const cells: any[] = []
        const trCls = (tr as HTMLElement).className
        for (const cell of Array.from(tr.querySelectorAll('th,td'))) {
          const el = cell as HTMLElement
          const isHeader = el.tagName === 'TH'
          const colspan = parseInt(el.getAttribute('colspan') ?? '1')
          const rowspan = parseInt(el.getAttribute('rowspan') ?? '1')
          const fill  = isHeader ? '0e69a0' : cellFill(el.className, trCls)
          const color = isHeader ? 'ffffff' : '1a1a1a'
          const bold  = isHeader
            || trCls.includes('fila-subtotal') || trCls.includes('fila-global')
            || el.className.includes('celda-factor')
          const right = el.className.includes('col-num')
          cells.push(new TableCell({
            ...(colspan > 1 ? { columnSpan: colspan } : {}),
            ...(rowspan > 1 ? { rowSpan: rowspan }  : {}),
            shading:  { fill, type: ShadingType.CLEAR, color: 'auto' },
            margins:  { top: 60, bottom: 60, left: 100, right: 100 },
            children: [new Paragraph({
              alignment: right ? AlignmentType.RIGHT : AlignmentType.LEFT,
              children:  [new TextRun({ text: el.textContent?.trim() ?? '', bold, color, size: isHeader ? 16 : 18, font: 'Calibri' })],
            })],
          }))
        }
        if (cells.length) rows.push(new TableRow({ children: cells }))
      }
      return new Table({
        rows,
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top:     { style: BorderStyle.SINGLE, size: 1, color: 'dddddd' },
          bottom:  { style: BorderStyle.SINGLE, size: 1, color: 'dddddd' },
          left:    { style: BorderStyle.SINGLE, size: 1, color: 'dddddd' },
          right:   { style: BorderStyle.SINGLE, size: 1, color: 'dddddd' },
          insideH: { style: BorderStyle.SINGLE, size: 1, color: 'dddddd' },
          insideV: { style: BorderStyle.SINGLE, size: 1, color: 'dddddd' },
        },
      })
    }

    const SKIP_TAGS = new Set(['BUTTON', 'INPUT', 'SELECT', 'STYLE', 'SCRIPT', 'NOSCRIPT'])
    const SKIP_CLS  = [
      'pagina-pie', 'controles', 'barra-superior', 'acciones-derecha',
      'portada-banda-bottom', 'portada-badge', 'pag-vacio', 'leyenda',
    ]

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const domADocx = async (el: HTMLElement): Promise<any[]> => {
      const tag = el.tagName?.toUpperCase() ?? ''
      const cls = (el.className as string) ?? ''

      if (SKIP_TAGS.has(tag)) return []
      if (SKIP_CLS.some(c => cls.includes(c))) return []

      // SVG → PNG image
      if (tag === 'SVG') {
        const png = await svgAPngBuffer(el as unknown as SVGSVGElement)
        if (!png) return []
        const MAX_W = 500, MAX_H = 340
        const ratio = Math.min(MAX_W / png.width, MAX_H / png.height, 1)
        return [new Paragraph({
          children: [new ImageRun({
            data: png.data,
            transformation: { width: Math.round(png.width * ratio), height: Math.round(png.height * ratio) },
            type: 'png',
          })],
          spacing: { after: 120 },
        })]
      }

      // TABLE → docx Table
      if (tag === 'TABLE') {
        return [tablaADocx(el as HTMLTableElement), new Paragraph({ text: '', spacing: { after: 80 } })]
      }

      // Headings
      if (tag === 'H1' || cls.includes('portada-h1')) {
        return [new Paragraph({
          children: [new TextRun({ text: el.textContent?.trim() ?? '', bold: true, size: 52, font: 'Calibri', color: '1a1a1a' })],
          spacing: { before: 400, after: 200 },
        })]
      }
      if (tag === 'H2') {
        return [new Paragraph({ children: nodosARuns(el, { bold: true }), heading: HeadingLevel.HEADING_1 })]
      }
      if (tag === 'H3') {
        return [new Paragraph({ children: nodosARuns(el, { bold: true }), heading: HeadingLevel.HEADING_2 })]
      }

      // Paragraph
      if (tag === 'P') {
        const text = el.textContent?.trim() ?? ''
        if (!text) return []
        if (cls.includes('pag-eyebrow') || cls.includes('portada-etiqueta')) {
          return [new Paragraph({ children: [new TextRun({ text, bold: true, size: 16, color: '0e69a0', font: 'Calibri' })] })]
        }
        const runs = nodosARuns(el)
        return [new Paragraph({ children: runs.length ? runs : [new TextRun({ text, size: 22, font: 'Calibri' })], spacing: { after: 80 } })]
      }

      // Horizontal divider -> small gap
      if (cls.includes('pag-divider') || cls.includes('factor-divider')) {
        return [new Paragraph({ text: '', spacing: { after: 80 } })]
      }

      // 'Caracteristicas' section label
      if (cls.includes('caract-header')) {
        return [new Paragraph({
          children: [new TextRun({ text: el.textContent?.trim() ?? '', bold: true, size: 18, color: '0e69a0', font: 'Calibri' })],
          spacing: { before: 200, after: 80 },
        })]
      }

      // UL / OL
      if (tag === 'UL') {
        return Array.from(el.querySelectorAll('li')).map(li =>
          new Paragraph({ text: li.textContent?.trim() ?? '', bullet: { level: 0 }, spacing: { after: 40 } })
        )
      }
      if (tag === 'OL') {
        return Array.from(el.querySelectorAll('li')).map((li, i) =>
          new Paragraph({ children: [new TextRun({ text: `${i + 1}. ${li.textContent?.trim() ?? ''}`, size: 22, font: 'Calibri' })], spacing: { after: 40 } })
        )
      }

      // KPI card -> inline label + value (skip child elements individually)
      if (cls.includes('kpi-card')) {
        const label = el.querySelector('.kpi-label')?.textContent?.trim() ?? ''
        const valor = el.querySelector('.kpi-valor')?.textContent?.trim() ?? ''
        return [new Paragraph({ children: [
          new TextRun({ text: label ? label + ': ' : '', size: 18, bold: true, font: 'Calibri' }),
          new TextRun({ text: valor, size: 22, bold: true, color: '0e69a0', font: 'Calibri' }),
        ], spacing: { after: 60 } })]
      }
      if (cls.includes('kpi-label') || cls.includes('kpi-valor')) return []

      // Resumen semaforo card
      if (cls.includes('resumen-card')) {
        const titulo = el.querySelector('.resumen-titulo')?.textContent?.trim() ?? ''
        const filas  = Array.from(el.querySelectorAll('.resumen-fila')).map(f => f.textContent?.trim()).join(', ')
        return [new Paragraph({ children: [
          new TextRun({ text: titulo ? titulo + ': ' : '', bold: true, size: 20, color: '0e69a0', font: 'Calibri' }),
          new TextRun({ text: filas, size: 20, font: 'Calibri' }),
        ], spacing: { after: 60 } })]
      }
      if (cls.includes('resumen-titulo') || cls.includes('resumen-fila')) return []

      // Default: recurse into children
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result: any[] = []
      for (const child of Array.from(el.children)) {
        result.push(...await domADocx(child as HTMLElement))
      }
      return result
    }

    // ── Build document ────────────────────────────────────────────

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const docChildren: any[] = []
    const pagElems = Array.from(area.querySelectorAll<HTMLElement>('[data-pagina-id]'))

    for (let i = 0; i < pagElems.length; i++) {
      if (i > 0) docChildren.push(new Paragraph({ pageBreakBefore: true, text: '' }))
      docChildren.push(...await domADocx(pagElems[i]))
    }

    const doc = new Document({
      styles: {
        default: {
          heading1: { run: { size: 30, bold: true, color: '0e69a0', font: 'Calibri' }, paragraph: { spacing: { before: 200, after: 100 } } },
          heading2: { run: { size: 22, bold: true, color: '1a56a0', font: 'Calibri' }, paragraph: { spacing: { before: 160, after: 80  } } },
          document:  { run: { size: 22, font: 'Calibri', color: '1a1a1a' }, paragraph: { spacing: { after: 80 } } },
        },
      },
      sections: [{
        properties: { page: { margin: { top: 1080, right: 1080, bottom: 1080, left: 1080 } } },
        children: docChildren,
      }],
    })

    const blob = await Packer.toBlob(doc)
    const nombre = (props.informe.titulo ?? 'Informe').replace(/[<>:"/\\|?*]/g, '').trim()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `${nombre || 'Informe'}.docx`
    document.body.appendChild(a); a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

  } catch (e) {
    console.error('[Word] Error generando documento:', e)
  } finally {
    generandoWord.value = false
    modoVista.value = prevModo
  }
}

onMounted(() => {
  if (props.visible) {
    paginas.value = buildPaginas()
    if (props.autoImprimir) {
      setTimeout(() => imprimirPdf(), 100)
    }
    if (props.autoDescargarWord) {
      setTimeout(() => descargarWord(), 100)
    }
  }
})

onBeforeUnmount(() => {
  limpiarImpresion()
})

const filasAMostrar = computed<Pagina[][]>(() => {
  if (modoVista.value === 'simple') {
    return paginas.value.map(p => [p])
  }
  const filas: Pagina[][] = []
  for (let i = 0; i < paginas.value.length; i += 2) {
    const fila: Pagina[] = [paginas.value[i]]
    if (paginas.value[i + 1]) fila.push(paginas.value[i + 1])
    filas.push(fila)
  }
  return filas
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    (_ctx.visible)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("span", _hoisted_4, _toDisplayString(_ctx.informe.titulo ?? 'Informe'), 1),
              _createElementVNode("span", {
                class: _normalizeClass(["badge-estado", _ctx.informe.estado])
              }, _toDisplayString(etiquetaEstado.value), 3)
            ]),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(modos, (m) => {
                  return _createElementVNode("button", {
                    key: m.id,
                    class: _normalizeClass(["modo-btn", { activo: modoVista.value === m.id }]),
                    onClick: ($event: any) => (modoVista.value = m.id)
                  }, _toDisplayString(m.label), 11, _hoisted_7)
                }), 64))
              ])
            ]),
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("span", _hoisted_9, _toDisplayString(paginas.value.length) + " sección" + _toDisplayString(paginas.value.length !== 1 ? 'es' : ''), 1),
              _createElementVNode("button", {
                class: "btn-word",
                disabled: generandoWord.value,
                onClick: descargarWord
              }, _toDisplayString(generandoWord.value ? 'Generando Word…' : 'Descargar Word'), 9, _hoisted_10),
              _createElementVNode("button", {
                class: "btn-pdf",
                disabled: generandoPdf.value,
                onClick: imprimirPdf
              }, _toDisplayString(generandoPdf.value ? 'Preparando…' : 'Descargar PDF'), 9, _hoisted_11),
              _createElementVNode("button", {
                class: "btn-cerrar-doc",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('cerrar')))
              }, "Cerrar")
            ])
          ]),
          _createElementVNode("div", _hoisted_12, [
            _createElementVNode("div", {
              ref_key: "paginasAreaRef",
              ref: paginasAreaRef,
              class: _normalizeClass(["paginas-area", `modo-${modoVista.value}`])
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filasAMostrar.value, (fila, fi) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: fi,
                  class: "fila-paginas"
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fila, (pag) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: pag.id,
                      class: "pagina",
                      "data-pagina-id": pag.id
                    }, [
                      (pag.tipo === 'portada')
                        ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                            _createElementVNode("div", _hoisted_15, [
                              _cache[1] || (_cache[1] = _createElementVNode("p", { class: "portada-etiqueta" }, "Informe de Autoevaluación", -1)),
                              _createElementVNode("h1", _hoisted_16, _toDisplayString(_ctx.informe.titulo ?? '—'), 1),
                              _createElementVNode("span", {
                                class: _normalizeClass(["badge-estado portada-badge", _ctx.informe.estado])
                              }, _toDisplayString(etiquetaEstado.value), 3)
                            ]),
                            _cache[2] || (_cache[2] = _createElementVNode("div", { class: "portada-banda-bottom" }, null, -1))
                          ]))
                        : (pag.tipo === 'general')
                          ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                              _createElementVNode("h2", _hoisted_18, _toDisplayString(pag.titulo), 1),
                              _cache[3] || (_cache[3] = _createElementVNode("div", { class: "pag-divider" }, null, -1)),
                              (bloquesFor(`gen-${pag.campo}`).length)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(bloquesFor(`gen-${pag.campo}`), (b) => {
                                      return (_openBlock(), _createBlock(BloqueDatos, {
                                        key: b.id,
                                        tipo: b.tipo,
                                        params: b.params ?? {},
                                        modoPreview: true
                                      }, null, 8, ["tipo", "params"]))
                                    }), 128))
                                  ]))
                                : _createCommentVNode("", true),
                              (textoGeneral(pag.campo) && !tieneTextoBloque(`gen-${pag.campo}`))
                                ? (_openBlock(), _createElementBlock("div", {
                                    key: 1,
                                    class: "pag-texto ql-content",
                                    innerHTML: textoGeneral(pag.campo)
                                  }, null, 8, _hoisted_20))
                                : (!bloquesFor(`gen-${pag.campo}`).length && !textoGeneral(pag.campo))
                                  ? (_openBlock(), _createElementBlock("p", _hoisted_21, " Sin contenido redactado. "))
                                  : _createCommentVNode("", true)
                            ]))
                          : (pag.tipo === 'factor' && pag.factor)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                                _cache[5] || (_cache[5] = _createElementVNode("p", { class: "pag-eyebrow" }, "Factor", -1)),
                                _createElementVNode("h2", _hoisted_23, _toDisplayString(pag.factor.nombre), 1),
                                _cache[6] || (_cache[6] = _createElementVNode("div", { class: "pag-divider factor-divider" }, null, -1)),
                                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(bloquesFactor, (bf) => {
                                  return (_openBlock(), _createElementBlock(_Fragment, {
                                    key: bf.clave
                                  }, [
                                    (bloquesFor(`comp-${pag.factor.id}-${bf.clave}`).length || redaccionHtml(pag.factor.id, bf.clave))
                                      ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                                          _createElementVNode("h3", _hoisted_25, _toDisplayString(bf.titulo), 1),
                                          (bloquesFor(`comp-${pag.factor.id}-${bf.clave}`).length)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_26, [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(bloquesFor(`comp-${pag.factor.id}-${bf.clave}`), (b) => {
                                                  return (_openBlock(), _createBlock(BloqueDatos, {
                                                    key: b.id,
                                                    tipo: b.tipo,
                                                    params: b.params ?? {},
                                                    modoPreview: true
                                                  }, null, 8, ["tipo", "params"]))
                                                }), 128))
                                              ]))
                                            : _createCommentVNode("", true),
                                          (redaccionHtml(pag.factor.id, bf.clave) && !tieneTextoBloque(`comp-${pag.factor.id}-${bf.clave}`))
                                            ? (_openBlock(), _createElementBlock("div", {
                                                key: 1,
                                                class: "pag-texto ql-content",
                                                innerHTML: redaccionHtml(pag.factor.id, bf.clave)
                                              }, null, 8, _hoisted_27))
                                            : _createCommentVNode("", true)
                                        ]))
                                      : _createCommentVNode("", true)
                                  ], 64))
                                }), 64)),
                                (pag.factor.hijos.length)
                                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                      _cache[4] || (_cache[4] = _createElementVNode("div", { class: "caract-header" }, "Características", -1)),
                                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pag.factor.hijos, (c) => {
                                        return (_openBlock(), _createElementBlock("div", {
                                          key: c.id,
                                          class: "caract-bloque"
                                        }, [
                                          _createElementVNode("h3", _hoisted_28, _toDisplayString(c.nombre), 1),
                                          (bloquesFor(`comp-${c.id}-analisis`).length)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(bloquesFor(`comp-${c.id}-analisis`), (b) => {
                                                  return (_openBlock(), _createBlock(BloqueDatos, {
                                                    key: b.id,
                                                    tipo: b.tipo,
                                                    params: b.params ?? {},
                                                    modoPreview: true
                                                  }, null, 8, ["tipo", "params"]))
                                                }), 128))
                                              ]))
                                            : _createCommentVNode("", true),
                                          (redaccionHtml(c.id, 'analisis') && !tieneTextoBloque(`comp-${c.id}-analisis`))
                                            ? (_openBlock(), _createElementBlock("div", {
                                                key: 1,
                                                class: "pag-texto ql-content",
                                                innerHTML: redaccionHtml(c.id, 'analisis')
                                              }, null, 8, _hoisted_30))
                                            : (!bloquesFor(`comp-${c.id}-analisis`).length && !redaccionHtml(c.id, 'analisis'))
                                              ? (_openBlock(), _createElementBlock("p", _hoisted_31, " Sin análisis redactado. "))
                                              : _createCommentVNode("", true)
                                        ]))
                                      }), 128))
                                    ], 64))
                                  : _createCommentVNode("", true)
                              ]))
                            : _createCommentVNode("", true),
                      _createElementVNode("div", _hoisted_32, [
                        _createElementVNode("span", _hoisted_33, _toDisplayString(_ctx.informe.titulo ?? 'Informe'), 1)
                      ])
                    ], 8, _hoisted_13))
                  }), 128))
                ]))
              }), 128))
            ], 2)
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})